import React, { createContext, useState, useContext, useMemo, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { loadEnvSettings } from 'system/envSettingsStore';
import {
  applyWorkspaceTabOpenLimit,
  formatTabCloseMessage,
  normalizeWorkspaceTabSettings,
} from 'system/workspaceTabSettings';

// TabContext 생성
const TabContext = createContext(null);

// TabProvider 컴포넌트
// enforceOpenLimit: 업무 화면 탭 상한(환경설정). 페이지 내부 탭(PageInnerTabs)은 false
export const TabProvider = ({ children, enforceOpenLimit = false }) => {
  const { compCd } = useAuth();
  const [tabs, setTabs] = useState([]); // 열린 탭 목록
  const [activeTabId, setActiveTabId] = useState(null); // 현재 활성 탭 ID

  // 탭 열기 - useCallback으로 메모이제이션
  // options.activate=false 이면 탭만 등록하고 활성 탭은 바꾸지 않음 (PageInnerTabs 일괄 등록용)
  const openTab = useCallback((tabData, options = {}) => {
    const { activate = true } = options;
    const settings = enforceOpenLimit
      ? normalizeWorkspaceTabSettings(loadEnvSettings(compCd).tabs)
      : null;
    setTabs(prevTabs => {
      // 이미 열려있는 탭인지 확인
      const existingTab = prevTabs.find(tab => tab.id === tabData.id);
      if (existingTab) {
        if (activate) {
          setActiveTabId(prevActiveTabId => {
            if (prevActiveTabId === tabData.id) {
              return prevActiveTabId;
            }
            return tabData.id;
          });
        }
        return prevTabs;
      }

      let baseTabs = prevTabs;
      if (settings) {
        const limited = applyWorkspaceTabOpenLimit(prevTabs, tabData, settings);
        baseTabs = limited.tabs;
        if (limited.closedTab) {
          const msg = formatTabCloseMessage(
            settings,
            limited.closedTab.label,
            tabData.label
          );
          if (msg) {
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent('erp:workspace-tab-closed', { detail: { message: msg } })
              );
            }, 0);
          }
        }
      }

      // 새 탭 추가 (refreshKey: 새로고침 시 컴포넌트 리마운트용)
      const newTab = {
        id: tabData.id,
        label: tabData.label,
        path: tabData.path,
        component: tabData.component,
        componentPath: tabData.componentPath,
        menuPath: Array.isArray(tabData.menuPath) ? tabData.menuPath : [],
        refreshKey: 0,
      };

      if (activate) {
        setActiveTabId(newTab.id);
      }
      return [...baseTabs, newTab];
    });
  }, [compCd, enforceOpenLimit]);

  // 탭 닫기 - useCallback으로 메모이제이션
  const closeTab = useCallback((tabId) => {
    setTabs(prevTabs => {
      const filteredTabs = prevTabs.filter(tab => tab.id !== tabId);
      
      // 닫은 탭이 활성 탭이었으면 다른 탭 활성화
      if (activeTabId === tabId) {
        if (filteredTabs.length > 0) {
          // 마지막 탭 활성화
          setActiveTabId(filteredTabs[filteredTabs.length - 1].id);
        } else {
          setActiveTabId(null);
        }
      }
      
      return filteredTabs;
    });
  }, [activeTabId]);

  // 모든 탭 닫기. homeTab 있으면 닫은 뒤 해당 홈 탭만 다시 연다.
  const closeAllTabs = useCallback((homeTab = null) => {
    if (homeTab && homeTab.id) {
      setTabs([
        {
          id: homeTab.id,
          label: homeTab.label,
          path: homeTab.path,
          component: homeTab.component,
          componentPath: homeTab.componentPath,
          menuPath: Array.isArray(homeTab.menuPath) ? homeTab.menuPath : [],
          refreshKey: 0,
        },
      ]);
      setActiveTabId(homeTab.id);
      return;
    }
    setTabs([]);
    setActiveTabId(null);
  }, []);

  // 탭 활성화 - useCallback으로 메모이제이션
  // 이미 활성화된 탭을 클릭하면 리렌더링하지 않도록 체크
  const activateTab = useCallback((tabId) => {
    setActiveTabId(prevActiveTabId => {
      // 같은 탭이면 업데이트하지 않음 (리렌더링 방지)
      if (prevActiveTabId === tabId) {
        return prevActiveTabId;
      }
      return tabId;
    });
  }, []);

  // 탭 새로고침 - 해당 탭 패널만 리마운트 (refreshKey 증가)
  const refreshTab = useCallback((tabId) => {
    setTabs(prevTabs =>
      prevTabs.map(tab =>
        tab.id === tabId ? { ...tab, refreshKey: (tab.refreshKey ?? 0) + 1 } : tab
      )
    );
  }, []);

  // value 객체를 useMemo로 메모이제이션하여 불필요한 리렌더링 방지
  const value = useMemo(() => ({
    tabs,
    activeTabId,
    openTab,
    closeTab,
    closeAllTabs,
    activateTab,
    refreshTab,
  }), [tabs, activeTabId, openTab, closeTab, closeAllTabs, activateTab, refreshTab]);

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
};

// useTab Hook
export const useTab = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTab must be used within a TabProvider');
  }
  return context;
};

